import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Divider } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 980,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    textAlign: 'left',

    [theme.breakpoints.down('sm')]: {
      padding: '10px 8px 0px',
    },
  },
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background:
      'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,

    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px 10px',
      marginBottom: 10,
    },
  }, 
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '2.0rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  subtitle: {
    marginTop: 8,
    color: '#cfd8ff',
    lineHeight: 1.6,
    fontSize: 13,
    maxWidth: 860,
    textAlign: 'left',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  section: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    padding: 16,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: '#e5e7ff',
    marginBottom: 10,
    textAlign: 'left',
  },
  body: {
    fontSize: 13,
    color: '#e8f5ff',
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
  },
  callout: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(158, 247, 197, 0.28)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  placeholder: {
    marginTop: 12,
    borderRadius: 12,
    border: '1px dashed rgba(255,255,255,0.22)',
    background: 'rgba(5,8,20,0.62)',
    padding: 12,
  },
  placeholderLabel: {
    fontSize: 12,
    color: '#cfd8ff',
    fontWeight: 800,
    marginBottom: 6,
  },
  placeholderHint: {
    fontSize: 12,
    color: '#9ea4d8',
    lineHeight: 1.55,
  },
  divider: {
    backgroundColor: 'rgba(255,255,255,0.10)',
    margin: '14px 0',
  },
}));

function ImagePlaceholder({ filename, description }) {
  const classes = useStyles();
  const src = `/story-guide/${filename}`;
  const [hasError, setHasError] = React.useState(false);
  return (
    <div className={classes.placeholder}>
      <div className={classes.placeholderLabel}>{filename}</div>
      <div className={classes.placeholderHint}>{description}</div>
      <div className={classes.placeholderHint} style={{ marginTop: 6 }}>
        Place this image at: <code>client/public/story-guide/{filename}</code>
      </div>
      <div style={{ marginTop: 10 }}>
        {!hasError && (
          <img
            src={src}
            alt={filename}
            style={{ width: '100%', borderRadius: 10, border: '1px solid rgba(255,255,255,0.10)' }}
            onError={() => setHasError(true)}
          />
        )}
        {hasError && (
          <div className={classes.placeholderHint}>
            (Screenshot not found yet at <code>{src}</code>)
          </div>
        )}
      </div>
    </div>
  );
}

export default function StoryGuidePage() {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>E3D Story Guide</div>
            <div className={classes.subtitle}>
              A practical walkthrough for using Stories + Evidence to understand what’s happening on-chain, why it matters, and how to drill down from a narrative to raw evidence.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price-guide')}>
              Price Guide
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/story-reference')}>
              Story Reference
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/story-use-cases')}>
              Use Cases
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/methodology')}>
              Methodology
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/stories')}>
              Back to Stories
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>1) The mental model</div>
        <div className={classes.body}>
          {'Stories are the “why.” Evidence is the “proof.”\n\nA story should quickly answer:\n- What happened?\n- Who/what was involved? (token, wallets, counterparties)\n- What changed? (volume, clustering, new counterparties, propagation)\n- Why should I care? (risk, opportunity, manipulation, coordination, migration)\n\nYour job: validate the narrative with evidence and decide whether it is meaningful.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>1b) Hedge-fund edge stories: what they mean in practice</div>
        <div className={classes.body}>
          {`These new stories are designed for crypto hedge fund workflows — spotting risk before it becomes obvious and surfacing accumulation before it becomes price.

- LIQUIDITY_DRAIN: pool TVL is falling fast while price has not fully caught up. Example: reduce size or exit before the pair becomes untradeable.
- EXCHANGE_FLOW: net deposits to exchanges often imply sell pressure; net withdrawals can imply accumulation or self-custody. Example: treat exchange inflows as a risk overlay on longs.
- MOMENTUM_DIVERGENCE: price and on-chain fundamentals are disagreeing. Example: price is rising while unique traders and real volume fall — a classic late-move warning.
- WASH_TRADE: volume is inflated by round-trips or suspicious recycling. Example: discount a pump if the flow looks manufactured.
- CONCENTRATION_SHIFT: top holders are gaining or losing share unusually quickly. Example: a concentration increase can mean a whale is building; a sharp drop can mean insiders are distributing.
- ACCUMULATION: a single whale is net buying a token at scale. Example: treat a large net inflow as a strong buy/watch signal, especially when the buyer has a clean track record.
- BREAKOUT_CONFIRMED: a MOVER story and an upgraded SURGE story both point at the same token. Example: use it as a higher-conviction momentum signal than price action alone.
- TOKEN_QUALITY_SCORE: a composite “first look” score for new tokens. Example: use it to rank new listings before spending analyst time on deep dive research.
- SMART_MONEY: profitable wallets are converging on the same token. Example: watch for a cluster of historically strong wallets buying the same asset in 24h.
- STEALTH_ACCUMULATION: many small buys are adding up to a large coordinated position. Example: spot whales splitting size across wallets to avoid drawing attention.
- INSIDER_TIMING: wallets bought before a major event or announcement. Example: review the pre-event accumulation trail after an airdrop or delegation event.
- VOLUME_PROFILE_ANOMALY: trading has shifted to unusual hours or repeated trade sizes. Example: sudden Asia-hours volume on a U.S.-focused token can hint at a new participant base or OTC-style distribution.
- SPREAD_WIDENING: slippage is worsening faster than trading activity explains. Example: liquidity is thinning while the market still looks active — a market-maker exit signal.`}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>2) Step-by-step workflow</div>
        <div className={classes.body}>
          {'Step A — Scan the feed\n- Use the Type/Chain filters and search to find what you care about.\n- Look for unusually high score, unusual story types, or narratives that imply coordination.\n\nStep B — Open the Evidence Summary\n- Confirm the “Primary token” is the right entry point.\n- The graph should show the center node and the most important related nodes.\n- Use the Type + Links columns to understand WHY each address is included and how connected it is.\n\nStep C — Drill down into the Evidence Graph (current route: /links)\n- This is the dedicated investigation view.\n- It should match the evidence snapshot you came from (same nodes/links).\n- From here you can explore, filter, and follow transactions.'}
        </div>
        <div className={classes.callout}>
          {'Naming note: “/links” deserves a clearer name. Suggested UI label: Evidence Graph.\n(We can keep the route as /links for now but rename the visible navigation labels.)'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>2b) How story integrity is measured</div>
        <div className={classes.body}>
          {'Every story now carries a visible integrity decomposition:\n\nC^S_t = a_E E + a_R R + a_I I + a_F F + a_C C\n\nDefault Phase 1 weights:\n- a_E (entity) = 0.20\n- a_R (reproducibility) = 0.30\n- a_I (impact) = 0.20\n- a_F (freshness) = 0.15\n- a_C (confidence) = 0.15\n\nEach story row also stores its family threshold tau_S and an admissible flag. Click "How scored" on a story card to inspect the five bars and see whether the composite cleared tau_S.'}
        </div>
        <div className={classes.callout}>
          {'Underlying equation: /methodology → Story Integrity.\nLive rates table: /methodology → Live Admissibility.\nOTA paper file: e3d_observable_transaction_admissibility_v1_thesis_convergence.docx'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>3) What to look for in the Evidence Summary</div>
        <div className={classes.body}>
          {'Signals to watch:\n- Tight clusters: small groups of wallets repeatedly interacting\n- Shared counterparties: multiple wallets using the same bridges, CEX deposit wallets, routers\n- Sudden new edges: brand new relationships (possible new campaign)\n- Hub-and-spoke patterns: one wallet orchestrating many wallets\n- Mirror patterns: groups moving in lockstep to the same counterparties\n\nCommon questions:\n- Are the “key” nodes real tokens/entities or just raw addresses?\n- Do the same names appear across multiple stories?\n- Does the structure suggest coordination or organic activity?'}
        </div>
        <ImagePlaceholder
          filename="01-evidence-summary-default.png"
          description="Screenshot the default Evidence Summary pane (not fullscreen). Show the graph and the node list with the Type + Links columns visible."
        />
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>4) Using fullscreen Evidence</div>
        <div className={classes.body}>
          {'Fullscreen is for doing a quick investigation without leaving Stories.\n- Expand to focus on the graph/list (especially on desktop or landscape).\n- Click nodes to promote them as the primary token/wallet and re-run the summary context.\n- Use the right-side list to jump between important nodes.'}
        </div>
        <ImagePlaceholder
          filename="02-evidence-summary-fullscreen.png"
          description="Screenshot the Evidence Summary in fullscreen mode on desktop/landscape (list on the right)."
        />
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>5) Evidence Graph (the dedicated investigation view)</div>
        <div className={classes.body}>
          {'From the Evidence Summary, click the “open in new” icon to open the dedicated Evidence Graph view (/links).\n\nPrimary graph note:\n- The Organic Galaxy layout is the primary transaction graph in E3D (the default).\n- Other layouts (Tube / Sphere / Grid / etc.) are supplementary — use them as alternate projections to reveal specific patterns, then return to Organic Galaxy to validate the real transaction neighborhood.\n\nRecommended investigation flow:\n- Start with the center node and its highest-degree neighbors\n- Look for suspicious hubs or repeated counterparties\n- Use view filters to isolate wallet-only or token-only connectivity\n- Zoom-to-fit and then zoom into subclusters\n\nIf the story implies a claim (e.g. coordination), find at least 2–3 independent signals in the graph before you trust it.'}
        </div>
        <ImagePlaceholder
          filename="03-evidence-graph-links-view.png"
          description="Screenshot the /links graph view after navigating from a story (should match the evidence snapshot)."
        />
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>6) Following transactions (graph → transactions)</div>
        <div className={classes.body}>
          {'Once you find a suspicious node:\n- Follow transactions from that wallet/token to see who it interacts with\n- Compare inbound vs outbound patterns\n- Watch for laundering patterns (many small hops, same intermediaries)\n\nPractical tip:\n- Try to follow one trail end-to-end: origin → intermediary → destination.'}
        </div>
        <ImagePlaceholder
          filename="04-follow-transactions-ui.png"
          description="Screenshot the UI controls for following transactions from a node in the graph (whatever the current UX is)."
        />
        <ImagePlaceholder
          filename="05-transactions-list-panel.png"
          description="Screenshot the transactions list/panel once transactions are loaded for a selected node."
        />
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>7) Other views worth using</div>
        <div className={classes.body}>
          {'Depending on what you’re investigating, use:\n- Token views (price / volatility / market cap) to see if on-chain behavior aligns with market movement\n- Chain view to distinguish ETH vs L2 vs BSC behavior\n- Saved maps / exports to share evidence\n\nA good investigation ends with something shareable:\n- a screenshot of the graph cluster\n- a list of key wallets\n- a small set of transactions showing the pattern'}
        </div>
        <Divider className={classes.divider} />
        <ImagePlaceholder
          filename="06-token-view-switcher.png"
          description="Screenshot the UI for switching between views (links/price/volatility/etc.)."
        />
        <ImagePlaceholder
          filename="07-export-or-share-controls.png"
          description="Screenshot export/share controls (PNG/JPG/PDF/CSV) if available." 
        />
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>8) Group mode (Story Grouping)</div>
        <div className={classes.body}>
          {'Grouping lets you combine multiple stories into one unified Evidence Summary.\n\nWhat it does:\n- You can select multiple stories and then turn on Group mode to build a single evidence graph/list from the union of all selected stories’ addresses.\n- This makes shared wallets, counterparties, and infrastructure (bridges, routers, CEX deposits, deployers) immediately visible as intersections.\n\nBest use cases:\n- Duplicate stories: multiple narratives about the same actor over time\n- Campaign detection: separate “events” that are actually one coordinated operation\n- Shared counterparties: multiple wallets interacting with the same bridge/CEX/router\n- Cross-chain patterns: the same entities showing up across chains or wrapped assets\n\nHow to use it (Stories page):\n1) Select stories for grouping\n- Click the People (group) icon on each story row/card to add/remove it from the selection.\n2) Enable Group mode\n- Click the global People icon in the Stories panel header. The number next to it shows how many stories are selected.\n3) Reset quickly\n- Click the X button next to the global People icon to clear the selection and exit Group mode.\n\nInterpreting the grouped graph:\n- Pay special attention to nodes that act as “connectors” between clusters (shared nodes).\n- If many stories intersect through one or two wallets, that’s often the highest-signal investigative lead.'}
        </div>
        <ImagePlaceholder
          filename="08-group-mode-intersections.png"
          description="Screenshot Group mode enabled with multiple stories selected, showing intersections/shared addresses in the evidence graph."
        />
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>9) Suggested checklist</div>
        <div className={classes.body}>
          {'Before you act on a story:\n- Do I understand the actors (token, wallets, counterparties)?\n- Does the graph structure support the narrative?\n- Can I identify at least one concrete transaction trail?\n- If it’s coordination: do multiple wallets share counterparties/timing?\n- If it’s a token: does chain activity align with price/volume narrative?'}
        </div>
      </div>
    </div>
  );
}
